import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { User, Lock, ShoppingBag, MapPin, ArrowRight, ArrowLeft, Truck } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import MaskedInput from '../../components/common/MaskedInput';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import Icon from '../../components/common/Icon';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { orderService } from '../../services/orderService';
import { authService } from '../../services/authService';
import { formatPrice } from '../../utils/priceCalculator';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

const ClientDashboard = () => {
  const { user, isAuthenticated, loading, updateUser } = useAuth();
  const { error, success } = useToast();

  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (user) setProfile({ name: user.name || '', email: user.email || '', phone: user.phone || '' });
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    if (!user) return undefined;
    setLoadingOrders(true);
    orderService
      .getAll({ limit: 5 })
      .then((data) => {
        if (!cancelled) setOrders(data?.rows || []);
      })
      .catch((e) => {
        if (!cancelled) error(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingOrders(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, error]);

  if (loading) return <Skeleton variant="block" height={120} />;
  if (!isAuthenticated) return <Navigate to="/entrar" replace />;

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {
        name: profile.name,
        email: profile.email,
        phone: profile.phone || null,
      };
      const res = await updateUser(payload);
      if (!res.success) {
        error(res.error || 'Erro ao salvar');
        return;
      }
      // Senha vai por endpoint dedicado /api/auth/password.
      if (showPasswordSection && passwordForm.newPassword) {
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
          error('Nova senha e confirmação não conferem');
          return;
        }
        try {
          await authService.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
          setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setShowPasswordSection(false);
          success('Perfil e senha atualizados');
          return;
        } catch (err) {
          error(err?.response?.data?.message || err?.message || 'Falha ao trocar senha');
          return;
        }
      }
      success('Perfil atualizado');
    } finally {
      setSavingProfile(false);
    }
  };

  // Próximo pedido = primeiro com status não-final (pending/approved/paid/shipped)
  const nextOrder = orders.find((o) =>
    ['pending', 'approved', 'awaiting_approval', 'paid', 'shipped'].includes(o.status)
  );

  return (
    <div className="max-w-6xl mx-auto">
      <Link
        to="/catalogo"
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline mb-3"
      >
        <ArrowLeft size={14} /> Voltar à loja
      </Link>
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-app-text">
          Olá, {user?.name?.split(' ')[0] || 'você'} 👋
        </h1>
        <p className="text-sm text-app-text-secondary">Gerencie pedidos, endereços e dados pessoais.</p>
      </div>

      {/* Próximo pedido em destaque */}
      {nextOrder && (
        <Card className="p-6 mb-6 bg-primary/5 border-primary/30">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Icon as={Truck} size="md" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-app-text-secondary uppercase tracking-wide mb-1">
                  Próximo pedido
                </p>
                <h2 className="text-lg font-bold text-app-text">
                  Pedido #{nextOrder.id}
                </h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <StatusBadge domain="order" status={nextOrder.status} size="sm" />
                  <span className="text-xs text-app-text-secondary">
                    {formatRelativeTime(nextOrder.createdAt)}
                  </span>
                </div>
                {nextOrder.trackingCode && (
                  <p className="text-xs text-app-text-secondary mt-2">
                    Rastreio:{' '}
                    <span className="font-mono font-semibold text-app-text">
                      {nextOrder.trackingCode}
                    </span>
                  </p>
                )}
              </div>
            </div>
            <Link to={`/minha-conta/pedidos/${nextOrder.id}`}>
              <Button size="sm" rightIcon={<ArrowRight size={14} />}>
                Ver detalhes
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Shortcuts */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Link to="/minha-conta/pedidos">
          <Card className="p-5 hover:shadow-lg transition-all h-full flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Icon as={ShoppingBag} size="md" className="text-primary" />
              <span className="font-semibold text-app-text">Pedidos</span>
            </div>
            <ArrowRight size={16} className="text-app-text-secondary" />
          </Card>
        </Link>
        <Link to="/minha-conta/enderecos">
          <Card className="p-5 hover:shadow-lg transition-all h-full flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Icon as={MapPin} size="md" className="text-primary" />
              <span className="font-semibold text-app-text">Endereços</span>
            </div>
            <ArrowRight size={16} className="text-app-text-secondary" />
          </Card>
        </Link>
        <Link to="/catalogo">
          <Card className="p-5 hover:shadow-lg transition-all h-full flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Icon as={ShoppingBag} size="md" className="text-primary" />
              <span className="font-semibold text-app-text">Explorar lojas</span>
            </div>
            <ArrowRight size={16} className="text-app-text-secondary" />
          </Card>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Perfil */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon as={User} size="md" className="text-primary" />
            <h2 className="text-lg font-bold text-app-text">Meus dados</h2>
          </div>
          <div className="space-y-3">
            <Input
              label="Nome"
              value={profile.name}
              onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
            />
            <Input
              label="Email"
              type="email"
              value={profile.email}
              onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
            />
            <MaskedInput
              label="Telefone"
              mask="phone"
              value={profile.phone}
              onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
              placeholder="(11) 98765-4321"
            />
            {user?.cpf && (
              <Input
                label="CPF"
                value={user.cpf}
                readOnly
                helpText="CPF não pode ser alterado pelo titular. Contate o suporte se necessário."
              />
            )}

            <button
              type="button"
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              onClick={() => setShowPasswordSection((v) => !v)}
            >
              <Lock size={14} />
              {showPasswordSection ? 'Cancelar troca de senha' : 'Alterar senha'}
            </button>

            {showPasswordSection && (
              <div className="space-y-2 pt-2 border-t border-app-border">
                <Input
                  type="password"
                  label="Senha atual"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))}
                />
                <Input
                  type="password"
                  label="Nova senha"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))}
                  helpText="Mínimo 6 caracteres"
                />
                <Input
                  type="password"
                  label="Confirmar nova senha"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                />
              </div>
            )}

            <Button fullWidth onClick={saveProfile} loading={savingProfile}>
              Salvar perfil
            </Button>
          </div>
        </Card>

        {/* Pedidos recentes */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-app-text">Pedidos recentes</h2>
            <Link to="/minha-conta/pedidos">
              <Button size="sm" variant="ghost" rightIcon={<ArrowRight size={14} />}>
                Ver todos
              </Button>
            </Link>
          </div>

          {loadingOrders ? (
            <Skeleton variant="text" lines={4} />
          ) : orders.length === 0 ? (
            <EmptyState
              icon={ShoppingBag}
              title="Sem pedidos"
              description="Comece explorando nosso catálogo."
              action={
                <Link to="/catalogo">
                  <Button size="sm">Ver catálogo</Button>
                </Link>
              }
            />
          ) : (
            <ul className="divide-y divide-app-border">
              {orders.map((o) => (
                <li key={o.id}>
                  <Link
                    to={`/minha-conta/pedidos/${o.id}`}
                    className="flex items-center gap-3 py-3 hover:bg-app-surface-hover transition-colors rounded-md px-2 -mx-2"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-app-text">Pedido #{o.id}</p>
                      <p className="text-xs text-app-text-secondary">
                        {formatRelativeTime(o.createdAt)} · {(o.items || []).length} item(s)
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-app-text">
                        {formatPrice(parseFloat(o.totalToPay || 0))}
                      </p>
                      <StatusBadge domain="order" status={o.status} size="sm" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ClientDashboard;
